import { Popup } from '@approvalmax/ui';
import { hooks } from '@approvalmax/utils';
import { selectors } from 'modules/common';
import { TwoFaActionWizard } from 'modules/components';
import { backend } from 'modules/data';
import { useSelector } from 'modules/react-redux';
import { getRequest } from 'pages/requestForm/selectors/pageSelectors';
import { memo, useCallback, useState } from 'react';

import { deepen } from './AddBeneficiaryPopup.helpers';
import {
    useCheckAvailablePaymentMethods,
    useCreateBeneficiaryMutation,
    useFieldsSchema,
    useIsInitialCurrencySupported,
    useKeepValidationErrorsUpdated,
    useNestedPopups,
    usePrepopulateFormValuesFromDefaultValues,
    useReloadSchemaIfLocalClearingFieldIsRequired,
} from './AddBeneficiaryPopup.hooks';
import { messages } from './AddBeneficiaryPopup.messages';
import { Body, DefaultContent, StyledLoadingBar } from './AddBeneficiaryPopup.styles';
import { AddBeneficiaryPopupProps, BeneficiaryPayload } from './AddBeneficiaryPopup.types';
import NavigationButtons from './components/NavigationButtons/NavigationButtons';
import Skeleton from './components/Skeleton/Skeleton';
import StepFour from './components/StepFour/StepFour';
import StepOne from './components/StepOne/StepOne';
import StepsIndicator from './components/StepsIndicator/StepsIndicator';
import StepThree from './components/StepThree/StepThree';
import StepTwo from './components/StepTwo/StepTwo';
import SuccessPopup from './components/SuccessPopup/SuccessPopup';

const AddBeneficiaryPopup = memo<AddBeneficiaryPopupProps>((props) => {
    const { closePopup, initialCurrency, initialBillId } = props;
    const [activeStep, setActiveStep] = useState(1);
    const prevActiveStep = hooks.usePrevious(activeStep);

    const {
        isOpen2FA,
        setIsOpen2FA,
        close2FAPopup,
        isOpenSuccessPopup,
        setIsOpenSuccessPopup,
        isUnsavedChangesPopupOpen,
        closeUnsavedChangesPopup,
        openUnsavedChangesPopup,
    } = useNestedPopups();

    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [beneficiaryPayload, setBeneficiaryPayload] = useState<BeneficiaryPayload>({});
    const [formValues, setFormValues] = useState<Record<string, string>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, boolean>>({});
    const [fieldsTouched, setFieldsTouched] = useState<Record<string, boolean>>({});
    const prevFormValues = hooks.usePrevious(formValues);
    const request = useSelector(getRequest);
    const integration = useSelector((state) =>
        selectors.integration.getIntegrationByCompanyId(state, request.companyId)
    );

    const [fieldsSchemaTransfer, setFieldsSchemaTransfer] =
        useState<backend.transfers.AirwallexBeneficiarySchemaTransfer>({
            companyId: request.companyId,
            accountCurrency: initialCurrency,
        });

    const { data: fieldsSchema, isInitialLoading: isLoadingFieldsSchema } = useFieldsSchema(fieldsSchemaTransfer);
    const prevFieldsSchema = hooks.usePrevious(fieldsSchema);

    const { mutateAsync: createBeneficiaryApiCall, isLoading: isLoadingCreateBeneficiary } =
        useCreateBeneficiaryMutation({
            companyId: request.companyId,
            requestId: request.id,
            initialBillId,
            setIsOpenSuccessPopup,
            setErrorMessages,
            setIsOpen2FA,
            fieldsSchema,
        });

    useIsInitialCurrencySupported({
        activeStep,
        prevFieldsSchema,
        fieldsSchema,
        closePopup,
        initialCurrency,
    });

    usePrepopulateFormValuesFromDefaultValues({
        fieldsSchema,
        fieldsTouched,
        formValues,
        prevFieldsSchema,
        setFormValues,
        initialCurrency: initialCurrency,
        integrationCountry: integration?.countryCode,
    });

    useReloadSchemaIfLocalClearingFieldIsRequired({
        activeStep,
        fieldsSchema,
        formValues,
        prevFormValues,
        companyId: request.companyId,
        setFieldsSchemaTransfer,
        fieldsSchemaTransfer,
    });

    useCheckAvailablePaymentMethods({
        activeStep,
        fieldsSchema,
        prevFieldsSchema,
        setFieldsSchemaTransfer,
        setActiveStep,
    });

    useKeepValidationErrorsUpdated({
        fieldsSchema,
        prevFieldsSchema,
        formValues,
        activeStep,
        prevActiveStep,
        validationErrors,
        setValidationErrors,
    });

    const createBenefeciary = useCallback(() => {
        const beneficiaryFieldsKeys = fieldsSchema?.map((field) => field.path) || [];

        const beneficiaryFields = beneficiaryFieldsKeys.reduce<Record<string, string>>((acc, key) => {
            acc[key] = formValues[key];

            return acc;
        }, {});

        const res = deepen(beneficiaryFields);

        setErrorMessages([]);
        setIsOpen2FA(true);
        setBeneficiaryPayload(res);
    }, [fieldsSchema, formValues, setIsOpen2FA]);

    const onEnterAppCode = useCallback(
        async (code: string) => {
            await createBeneficiaryApiCall({
                oneTimePassword: code,
                beneficiaryFields: {
                    ...beneficiaryPayload,
                    payment_methods: [beneficiaryPayload.payment_methods || ''],
                },
            });
        },
        [beneficiaryPayload, createBeneficiaryApiCall]
    );

    return (
        <>
            {isOpenSuccessPopup && <SuccessPopup closePopup={closePopup} />}

            {!isOpenSuccessPopup && (
                <DefaultContent
                    title={messages.popupTitle}
                    buttons={
                        <NavigationButtons
                            activeStep={activeStep}
                            createBenefeciary={createBenefeciary}
                            fieldsSchema={fieldsSchema || []}
                            formValues={formValues}
                            isLoadingFieldsSchema={isLoadingFieldsSchema}
                            companyId={request.companyId}
                            validationErrors={validationErrors}
                            setFieldsSchemaTransfer={setFieldsSchemaTransfer}
                            setActiveStep={setActiveStep}
                            setErrorMessages={setErrorMessages}
                        />
                    }
                    customCloseHandler={openUnsavedChangesPopup}
                >
                    <Body>
                        <StyledLoadingBar progress={isLoadingCreateBeneficiary} />

                        <StepsIndicator activeStep={activeStep - 1} hide={isLoadingCreateBeneficiary} />

                        {isLoadingFieldsSchema && <Skeleton />}

                        {activeStep === 1 && !isLoadingFieldsSchema && (
                            <StepOne
                                fields={fieldsSchema}
                                formValues={formValues}
                                setFormValues={setFormValues}
                                fieldsTouched={fieldsTouched}
                                setFieldsTouched={setFieldsTouched}
                            />
                        )}

                        {activeStep === 2 && !isLoadingFieldsSchema && (
                            <StepTwo
                                fields={fieldsSchema}
                                formValues={formValues}
                                setFormValues={setFormValues}
                                fieldsTouched={fieldsTouched}
                                setFieldsTouched={setFieldsTouched}
                            />
                        )}

                        {activeStep === 3 && !isLoadingFieldsSchema && (
                            <StepThree
                                fields={fieldsSchema}
                                formValues={formValues}
                                setFormValues={setFormValues}
                                validationErrors={validationErrors}
                                setValidationErrors={setValidationErrors}
                                fieldsTouched={fieldsTouched}
                                setFieldsTouched={setFieldsTouched}
                            />
                        )}

                        {activeStep === 4 && (
                            <StepFour
                                fields={fieldsSchema}
                                formValues={formValues}
                                errorMessages={errorMessages}
                                isLoading={isLoadingCreateBeneficiary}
                            />
                        )}

                        <TwoFaActionWizard
                            title={messages.twoFaPopupTitle}
                            open={isOpen2FA}
                            onEnterAppCode={onEnterAppCode}
                            onClose={close2FAPopup}
                        />

                        <Popup
                            isOpen={isUnsavedChangesPopupOpen}
                            onRequestClose={closeUnsavedChangesPopup}
                            disableAutoClose
                        >
                            <Popup.ConfirmationContent
                                onConfirm={closeUnsavedChangesPopup}
                                onCancel={closePopup}
                                title={messages.confirmationPopupTitle}
                                description={messages.confirmationPopupDescription}
                                confirmButtonText={messages.confirmButtonText}
                                confirmButtonTitle={messages.confirmButtonTitle}
                                cancelButtonText={messages.cancelButtonText}
                                cancelButtonTitle={messages.cancelButtonTitle}
                            />
                        </Popup>
                    </Body>
                </DefaultContent>
            )}
        </>
    );
});

export default AddBeneficiaryPopup;
